import { useArtwork } from "@design-lab/entities/artwork";
import {
  proceedWithActionAtom,
  useGreenScreenVerification
} from "@design-lab/features/green-screen-verification";
import { useSaveProject } from "@design-lab/features/save-project/hooks/useSaveProject";
import { cn } from "@design-lab/shared";
import { useEditMode } from "@design-lab/shared/hooks/useEditMode";
import { useProcessingTooltip } from "@design-lab/shared/hooks/useProcessingTooltip";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { trackDesignLabCtaClicked } from "@design-lab/shared/lib/analytics";
import { Button } from "@design-lab/shared/ui";
import { useTab } from "@design-lab/shared/ui/layouts/state";
import { Tooltip, TooltipContent, TooltipTrigger } from "@design-lab/shared/ui/tooltip";
import { atom, useAtom } from "jotai";
import { ChevronLeft, ChevronRight, Save } from "lucide-react";
import { useEffect } from "react";

// Shared atoms for loading states
export const saveDesignLoadingAtom = atom(false);
export const addToCartLoadingAtom = atom(false);

export function MobileButtonNavigation() {
  const { isValid, canGoNext, canGoBack, next, back, nextTab, prevTab, tab } = useTab();
  const { front, back: backArtwork } = useArtwork();
  const { isDesktop } = useResponsive();
  const { isEditMode } = useEditMode();

  const { showInvitation } = useGreenScreenVerification();
  const [proceedWithAction, setProceedWithAction] = useAtom(proceedWithActionAtom);
  const { setIsSaveProjectVisible } = useSaveProject();

  const frontHasLoading = front.state.hasTag("loading");
  const backHasLoading = backArtwork.state.hasTag("loading");

  const shouldShowTooltip = frontHasLoading || backHasLoading;
  const { showTooltip, dismissTooltip } = useProcessingTooltip(shouldShowTooltip);

  const [isSaveDesignLoading] = useAtom(saveDesignLoadingAtom);
  const [isAddingToCart] = useAtom(addToCartLoadingAtom);

  const handleNextClick = () => {
    if (showInvitation({ currentTab: tab, targetTab: "front", isEditMode })) return;
    next();
  };

  const handleSaveAndStartNew = async () => {
    trackDesignLabCtaClicked("save_and_start_new_design");
    setIsSaveProjectVisible(true);
  };

  // Calculate loading states
  const isAnyLoading = isAddingToCart || isSaveDesignLoading;
  const isBackButtonDisabled = isAnyLoading;
  const isSaveDesignDisabled = isAddingToCart || isSaveDesignLoading;

  useEffect(() => {
    if (proceedWithAction && !isDesktop) {
      if (canGoNext && nextTab) {
        next();
      } else {
        handleSaveAndStartNew();
      }
      setProceedWithAction(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proceedWithAction, isDesktop]);

  return (
    <div className="flex w-full gap-2">
      {canGoBack && prevTab && (
        <Button
          data-testid="back-button"
          variant="secondary"
          onPress={back}
          isDisabled={isBackButtonDisabled}
          className="bg-background h-12 flex-1"
        >
          <ChevronLeft />
          Back: {prevTab.mobileTitle || prevTab.title}
        </Button>
      )}

      {canGoNext && nextTab ? (
        <Tooltip open={showTooltip}>
          <TooltipTrigger asChild data-testid="next-button-tooltip-trigger">
            <Button
              data-testid="next-button"
              isDisabled={!isValid}
              onPress={handleNextClick}
              className={cn("h-12 bg-blue-700 hover:bg-blue-800", canGoBack ? "flex-1" : "w-full")}
            >
              Next: {nextTab.mobileTitle || nextTab.title} <ChevronRight />
            </Button>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            align="end"
            onClose={dismissTooltip}
            className="bg-blue-700"
            arrowClassName="bg-blue-700 fill-blue-700"
          >
            <p className="text-sm">
              While the image is being processed continue to add colors and more products.
            </p>
          </TooltipContent>
        </Tooltip>
      ) : (
        <Button
          data-testid="save-start-new-button"
          onPress={handleSaveAndStartNew}
          isDisabled={isSaveDesignDisabled}
          className={cn(
            "bg-background text-secondary-foreground h-12 border border-blue-700",
            canGoBack ? "flex-1" : "w-full"
          )}
        >
          <Save className="size-5" />
          Save Project
        </Button>
      )}
    </div>
  );
}
